import { escapeHTML } from '../../utils/escape.js';
import { ReservationCard, ReservationRow } from './ReservationRow.js';
import { RESERVATION_COLUMNS, RESERVATION_TH } from './reservationColumns.js';

/**
 * Grupo de reservas por DÍA dentro del panel de staff (product pivot,
 * reservation_status_model_v2 — antes era por momento+area). Función de
 * render pura: devuelve un string HTML, sin estado ni efectos. El header
 * muestra la fecha (formateada en español) y el resumen de ocupación por
 * área del día (Req: Per-Day Per-Area Capacity Summary), y delega cada fila
 * a `ReservationRow`.
 */

/**
 * Formatea una fecha ISO (`YYYY-MM-DD`) en español, ej. "Viernes, 10 de
 * julio". Construye el `Date` a partir de sus componentes año/mes/día
 * (hora local), NO vía `new Date(fecha)` (que interpreta un date-only string
 * como UTC medianoche y puede correrse un día según la zona horaria del
 * navegador) — mismo criterio anti-TZ-shift que `todayLocalISO` en
 * admin.js/admin-reservations.service.js.
 * @param {string} fecha
 * @returns {string}
 */
function formatFechaEs(fecha) {
    const [year, month, day] = fecha.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    const raw = new Intl.DateTimeFormat('es-AR', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
    }).format(date);
    return raw.charAt(0).toUpperCase() + raw.slice(1);
}

/**
 * @param {{ area: string, ocupadas: number, capacidad: number, ratio: number }[]} areas
 * @returns {string} ej. "Vereda 26/40 · Salón 34/50"
 */
function formatAreasSummary(areas) {
    return areas
        .map((a) => `${escapeHTML(a.area)} ${escapeHTML(String(a.ocupadas))}/${escapeHTML(String(a.capacidad))}`)
        .join(' · ');
}


/**
 * @param {{ fecha: string, rows: Array<object>, areas: Array<{ area: string, ocupadas: number, capacidad: number, ratio: number }> }} group
 * @returns {string} HTML del grupo del día (header con resumen de ocupación + tabla).
 */
export const ReservationGroup = (group) => {
    const { fecha, rows, areas } = group;
    const fechaLabel = escapeHTML(formatFechaEs(fecha));
    const areasSummary = formatAreasSummary(areas ?? []);

    const headerCells = RESERVATION_COLUMNS.map(({ label }) => {
        const align = label === 'Personas' ? 'text-center' : label === 'Acciones' ? 'text-right' : 'text-left';
        return `<th scope="col" class="${RESERVATION_TH} ${align}">${label}</th>`;
    }).join('');

    const colGroup = RESERVATION_COLUMNS.map(({ width }) => `<col style="width:${width}%" />`).join('');

    return `
        <section class="mb-8" aria-label="${fechaLabel}">
            <div class="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 mb-3">
                <h3 class="text-sm font-black uppercase tracking-widest text-text-primary">
                    ${fechaLabel}
                </h3>
                ${areasSummary ? `<p class="text-xs text-text-muted">${areasSummary}</p>` : ''}
            </div>
            <!-- Desktop (lg+): tabla densa. En mobile se oculta (7 columnas no entran). -->
            <div class="hidden lg:block bg-surface border border-border-subtle rounded-2xl overflow-hidden">
                <table class="w-full table-fixed text-left border-collapse">
                    <colgroup>${colGroup}</colgroup>
                    <thead class="border-b border-border-subtle">
                        <tr>${headerCells}</tr>
                    </thead>
                    <tbody>
                        ${rows.map((row) => ReservationRow(row)).join('')}
                    </tbody>
                </table>
            </div>
            <!-- Mobile (<lg): tarjetas apiladas, mismos datos, usables con el dedo. -->
            <div class="lg:hidden space-y-3">
                ${rows.map((row) => ReservationCard(row)).join('')}
            </div>
        </section>
    `;
};
